import { GROWTH_GLASS_SURFACE, GROWTH_SECTION_PADDING_X } from './growthStyles';

import { EmptyState } from '@/components/EmptyState/EmptyState';
import { GrowthPathIllustration } from '@/components/Illustrations/Illustration';
import { useAmelioTranslations, useNavigate } from '@/hooks';
import { useCompanyInformation } from '@/hooks/company/useCompanyInformation';
import { RouteName } from '@/navigation/types';

/** The tab's own breathing room, as the empty invitation next door draws it. */
const NOTICE_PADDING = `${GROWTH_SECTION_PADDING_X} py-12`;

/** The size every illustration of the module is drawn at; the width follows its own ratio. */
const ILLUSTRATION_SIZE = 'h-32 w-auto';

interface GrowthForbiddenNoticeProps {
    /**
     * Which of the two refusals this states. They are not interchangeable: the tab is closed on
     * one's own profile by rule, and closed on an employee the reader does not manage by scope --
     * a former employee, whose profile stays open to HR while this tab does not.
     */
    reason: 'ownProfile' | 'outOfScope';
    dataTestId?: string;
}

/**
 * Why the "Croissance" tab is closed. Both refusals are rules rather than failures, so this is
 * drawn like the tab's other empty states and never in the error colour.
 *
 * Only the reader's own profile gets a way out: the portal is where they see their own growth, and
 * it holds nothing about anyone else.
 */
export const GrowthForbiddenNotice = ({
    reason,
    dataTestId = 'growth-tab-forbidden',
}: GrowthForbiddenNoticeProps) => {
    const { t } = useAmelioTranslations();
    const { getManagerLabel } = useCompanyInformation();
    const navigate = useNavigate();

    return (
        <section data-testid={dataTestId}>
            <EmptyState
                className={`${GROWTH_GLASS_SURFACE} ${NOTICE_PADDING}`}
                illustration={<GrowthPathIllustration className={ILLUSTRATION_SIZE} />}
                title={t(`performanceProfile.growth.forbidden.${reason}.title`)}
                message={t(`performanceProfile.growth.forbidden.${reason}.description`, {
                    managerLabel: getManagerLabel(),
                })}
                action={
                    reason === 'ownProfile'
                        ? {
                              label: t('performanceProfile.growth.forbidden.ownProfile.viewPortal'),
                              onClick: () => navigate({ path: RouteName.EmployeePortal }),
                          }
                        : undefined
                }
            />
        </section>
    );
};
